/**
 * WizardPageV2 - Refactorización completa
 * 
 * Estructura (Cabecera B, dos filas — T1CB):
 * - Fila 1: HeaderSection persistente en App.tsx sobre <Routes> (no se renderiza aquí)
 * - Fila 2: WizardContextBar (contexto doc + stepper + acciones + progreso)
 * - NavArrows (solo con datos cargados)
 * - Contenido centrado con max-width 1100px
 * 
 * @version T1CB - Cabecera B fila 2 · FC.11 - Wizard 4 pasos: P1 Normaliza → P2 Valida → P3 Coteja → P4 Exporta
 */

import { useState, lazy, Suspense, useEffect, useCallback, useRef, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowCounterClockwise, X } from '@phosphor-icons/react'
// SERP2: por ruta de fichero, no por el barril (HX-MOCK-FACTORY-BARREL-COUPLING).
import { SpinnerSnake } from '../design-system/v2/components/SpinnerSnake'
import { colors as dsColors } from '../design-system/v2/design-tokens'
import { motion, AnimatePresence } from 'framer-motion'
import type { ExtractionResult } from './Step1'
import type { ProcessingComplete } from './Step2'
import { Step2Page } from './wizard-v2/step2-valida/Step2Page'
import { adaptStep2Props } from './wizard-v2/step2-valida/adaptStep2Props'
import { toast } from 'sonner'
import DataLoader from './DataLoader'
import {
  Step1Page,
  adaptExtractionResult,
  adaptInfrastructures,
  adaptMetadata,
  adaptMunicipioPendiente,
  adaptZeroInfraDiagnostic,
  type MunicipioPendienteDiagnostic,
  type ZeroInfraDiagnostic,
} from './wizard-v2/step1-normaliza'
// P1-S3 (esc. 4): catálogo INE maestro — solo lectura, nunca se edita
import { buscarCandidatosMunicipio, getMunicipioPorCodigo } from '../lib/codigosINEUnificado'
import { Button } from './ui/button'
import { useDocumentCache, type SerializableExtractionData, type WizardCacheState } from '../hooks/useDocumentCache'
import { useIsMounted } from '../hooks/useIsMounted'
import { extractInfrastructures } from '../lib/documentExtractor'
import { isAcceptedExtension, ACCEPTED_FORMATS_LABEL } from '../lib/acceptedFormats'
import { detectFormat } from '../utils/formatDetector'
import { useAutoGeocoding } from '../hooks/useAutoGeocoding'
import { useNormalization } from '../hooks/useNormalization'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { useAudit } from '../hooks/useAudit'
import { postCoordEdit, CoordEditError, MENSAJE_SIN_PERMISO, type CoordEditPayload } from '../services/coordEditsService'
import { revalidateRecord } from '../services/validation'
import { buildCoordEditPayload } from './wizard-v2/buildCoordEditPayload'
import { toExportMetadata } from './wizard-v2/step4-exporta/toExportMetadata'
import { filasPertenecenAlDocumento, mensajeFilasAjenas } from './wizard-v2/filasPertenecenAlDocumento'
import { construirProcessedData, huellaCotejo } from './wizard-v2/construirProcessedData'
import { useCotejo } from '../hooks/useCotejo'
import { useDecisionesFila } from '../hooks/useDecisionesFila'
import { useEdicionesP2, aplicarEdiciones } from '../hooks/useEdicionesP2'
import { toProcessedRecords } from '../services/export/toProcessedRecord'
import type { ResultadoCotejo } from '../services/cotejo/types'
import { aplicarDecisionesAlResultado } from '../services/cotejo/CotejoExportService'
import type { CotejoItem } from './wizard-v2/step3-coteja/cotejoItem'
import type { InfrastructureType } from '../types/infrastructure'
import { isIne5Duplicate, addIne5ToSession } from '../utils/detectIne5Duplicate'
import { precargarContorno } from '../services/boundaries/MunicipalBoundaryService'
import { detectSevereErrors } from '../services/analysis/errorDetector'
import type { ErrorSummary } from '../services/analysis/errorDetector'
// OBS-FN-02-A: helper central que resuelve la cascada geo > norm > raw
// y el predicado de «fila resuelta» (incluye status='manual').
import { pickInfrasForDisplay, pickInfrasForP1, countResolved, isResolved } from '../utils/pickInfrasForDisplay'

// Shell V2 - Componentes específicos del wizard
import { NavArrows } from './wizard-v2/shell/NavArrows'
// BUG-4: GeocodingProgressBar eliminado — ahora integrado en ProgressBar (Step1Page)
// T1CB: StepperFloating desmontado — el stepper vive en la fila 2 (WizardContextBar)
import type { StepId } from './wizard-v2/shell/StepperFloating'
import { WizardContextBar, DocumentStrip } from './wizard-v2/shell/WizardContextBar'
import { InfoBanner, BannerStrong } from './wizard-v2/shell/InfoBanner'
import { InlineEditForm } from './wizard-v2/step2-valida/InlineEditForm'
import { CoordEditPanel } from './wizard-v2/step2-valida/CoordEditPanel'
import { calculateBlobSHA256 } from '@/services/sync/DeltaChecker'

// Lazy loading
const Step3CotejaPage = lazy(() => import('./wizard-v2/step3-coteja').then(m => ({ default: m.Step3CotejaPage })))
const Step4ExportaPage = lazy(() => import('./wizard-v2/step4-exporta/Step3Page').then(m => ({ default: m.Step3Page })))

function StepLoading() {
  return (
    <div className="flex items-center justify-center py-16" data-testid="step-loading">
      <SpinnerSnake size={28} label="Cargando el paso" />
      <span className="ml-3 text-sm" style={{ color: dsColors.fg.secondary }}>Cargando…</span>
    </div>
  )
}

interface LocationState {
  file?: File
}

// ============================================
// SERVICE STATUS BADGE COMPONENT
// ============================================

interface ServiceBadgeProps {
  label: string
  status: 'online' | 'offline' | 'checking'
  icon: React.ReactNode
}

function ServiceBadge({ label, status, icon }: ServiceBadgeProps) {
  const colors = {
    online: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: '#22c55e' },
    offline: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444' },
    checking: { bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24' },
  }
  const c = colors[status]
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: 500,
      color: c.text,
    }}>
      {icon}
      {label}
    </span>
  )
}

// ============================================
// ADAPTADOR: ResultadoCotejo → CotejoItem[]
// ============================================

/**
 * Convierte ResultadoCotejo (servicio) en CotejoItem[] (UI Step3CotejaPage).
 * - matches → kind: 'incluido'
 * - ausentes → kind: 'ausente', urgencia derivada de criticidad
 * - sobrantes → kind: 'sobrante'
 */
function resultadoToCotejoItems(resultado: ResultadoCotejo): CotejoItem[] {
  const items: CotejoItem[] = []

  // Incluidos (matches confirmados/probables)
  for (const match of resultado.matches) {
    const inf = match.infraPTEL
    items.push({
      id: inf.id,
      nombre: inf.nombre,
      tipologia: (inf.tipo ?? 'GENERICO') as InfrastructureType,
      kind: 'incluido',
      urgencia: null,
      x: inf.xFinal,
      y: inf.yFinal,
    })
  }

  // Ausentes
  for (const ausente of resultado.ausentes) {
    const cand = ausente.candidato
    const crit: string = ausente.criticidad
    items.push({
      id: cand.idFuente,
      nombre: cand.nombre,
      tipologia: cand.tipologia,
      kind: 'ausente',
      urgencia: crit === 'critica' || crit === 'alta' ? 'alta'
               : crit === 'media' ? 'media'
               : 'baja',
      x: cand.x25830,
      y: cand.y25830,
      // OL-04: propaga destino catastral DGC → badge normativo de máxima precisión
      destinoCat: cand.destino_cod,
      // FC.06h: `metadata` con fuente del inventario de referencia (m³ de DERA G3,
      // BIC, potencia). Ya no lleva aforos estimados (`ALBCOT`); hoy ningún
      // componente de P3 la pinta.
      metadatos: cand.metadata,
    })
  }

  // Sobrantes
  for (const sobrante of resultado.sobrantes) {
    const inf = sobrante.infraPTEL
    items.push({
      id: inf.id,
      nombre: inf.nombre,
      tipologia: (inf.tipo ?? 'GENERICO') as InfrastructureType,
      kind: 'sobrante',
      urgencia: null,
      x: inf.xFinal,
      y: inf.yFinal,
    })
  }

  return items
}

// ============================================
// MAIN COMPONENT
// ============================================

export function WizardPageV2() {
  const location = useLocation()
  const navigate = useNavigate()
  const { step: urlStep } = useParams<{ step?: string }>()
  const locationState = location.state as LocationState | null
  
  const initialStep = urlStep ? (parseInt(urlStep, 10) as 1 | 2 | 3 | 4) : 1
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(
    [1, 2, 3, 4].includes(initialStep) ? initialStep : 1
  )
  const [completedSteps, setCompletedSteps] = useState<StepId[]>([])
  const [extractionData, setExtractionData] = useState<ExtractionResult | null>(null)
  const [processedData, setProcessedData] = useState<ProcessingComplete | null>(null)
  const [dataReady, setDataReady] = useState(false)
  
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStatus, setProcessingStatus] = useState('')
  const [processingError, setProcessingError] = useState<string | null>(null)
  // P1-S2 (esc. 3): diagnóstico de documento válido con 0 infraestructuras
  const [zeroInfraDiagnostic, setZeroInfraDiagnostic] = useState<ZeroInfraDiagnostic | null>(null)
  // P1-S3 (esc. 4): extracción retenida a la espera de confirmar municipio.
  // El INE condiciona la cascada de geolocalización y la validación →
  // BLOQUEAR el procesamiento hasta confirmarlo (decisión 4a). Se conserva
  // la extracción completa para reanudar sin re-parsear el documento.
  const [municipioPendiente, setMunicipioPendiente] = useState<{
    file: File
    extracted: Awaited<ReturnType<typeof extractInfrastructures>>
    diagnostic: MunicipioPendienteDiagnostic
  } | null>(null)

  // SSCA-FIX-1: guard de montaje para los setState tras await de handleFileSelect.
  const isMounted = useIsMounted()

  // INFDOC-3a (DOP-8 a de INFDOC-0): huella `sha256` del documento, atada a su
  // `File`. Solo se calcula con el fichero real de la carga; el que recrea
  // «Recuperar» (`fromSerializable`, cuerpo vacío) no tiene huella.
  const [huellaDocumento, setHuellaDocumento] = useState<{ file: File; sha256: string } | null>(null)

  // P1PROV-G1 (DOP-1 a): el documento activo, leído al terminar la tanda de
  // geolocalización para no dar por completado P1 de un documento que ya se
  // canceló o se cambió mientras geolocalizaba (CAMDOC).
  const extractionDataRef = useRef<ExtractionResult | null>(null)
  extractionDataRef.current = extractionData

  // P1PROV-G1 (DOP-1 a): P1 solo cuenta como completado cuando termina su
  // pipeline (extraer → normalizar → geolocalizar). De esta marca dependen la
  // flecha lateral, las pastillas del cabecero y la animación de «siguiente»;
  // antes se ponía al terminar la extracción y se podía llegar hasta P4 y
  // exportar con la nota de lectura (Jubrique, 31-oct-2026: P4 dijo 91 de
  // media en vez de 72 y no se puso al día al terminar).
  const marcarP1Completado = useCallback(() => {
    setCompletedSteps(prev => (prev.includes(1) ? prev : [...prev, 1 as StepId]))
  }, [])

  // C1: registro de INE5 cargados en esta sesión (detección de duplicados)
  const [sessionIne5s, setSessionIne5s] = useState<Set<string>>(new Set())

  // GUIA-DEVOLUCION-02: banner ámbar
  const [severeErrorSummary, setSevereErrorSummary] = useState<ErrorSummary | null>(null)
  
  // Estado para indicar que un paso acaba de completarse (activa animaciones)
  const [justCompletedStep, setJustCompletedStep] = useState<number | null>(null)
  
  const { hasCache, saveState, restoreState, clearCache, toSerializable, fromSerializable, cachedState: _cachedState } = useDocumentCache()
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false)
  const [cachedExtractionData, setCachedExtractionData] = useState<SerializableExtractionData | null>(null)
  // RECEXT (DOP-3 a): lo que leyó el banner al montar. «Recuperar» restaura
  // esto mismo y no una segunda lectura de sessionStorage, que entre el banner
  // y el clic puede haber cambiado (el banner anuncia un fichero concreto).
  const cachedStateRef = useRef<WizardCacheState | null>(null)
  
  // === NORMALIZACIÓN Y GEOCODIFICACIÓN ===
  const {
    normalizeAll,
    results: normalizedResultsMotor,
    isNormalizing,
    progress: normalizationProgress,
    stats: _normStats,
    patchResult,
    reset: resetNormalization,
  } = useNormalization()

  // C2: auth elevado aquí para que handleApplyCoords disponga de `user` en sus deps.
  const { user, sessionExpired } = useAuth()

  // S1-4a: destructuring elevado por encima de handleApplyCoords para que el
  // doble-patch (OBS-P2-PATCH-C2-FUENTE-GEOCODIFICADA) disponga de
  // patchGeocodedResult. Llamada incondicional: el orden de hooks entre renders
  // no varía.
  const {
    geocodeNeeded,
    isGeocoding,
    progress: geoProgress,
    results: geocodedResultsMotor,
    // P1PROV-a: solo para la tabla de P1 (`step1Props`); los *mocks* antiguos
    // del hook no lo traen → `undefined`, que la mezcla trata como «sin tanda».
    parcial: geoParcial,
    stats: geoStats,
    patchResult: patchGeocodedResult,
    reset: resetGeocoding,
  } = useAutoGeocoding()

  // C0-e3 (DOP-1 a): lo corregido en P2, por id de fila, aplicado al leer encima
  // de lo que den los motores. Sobrevive a que se rehagan (volver a P2 tras
  // «Recuperar») y a que la geolocalización termine con las filas con que
  // empezó (AUDIT_C0E3 H1). Sin ediciones, mismo array que el motor (H2).
  const {
    ediciones: edicionesP2,
    editar: editarFilaP2,
    vaciar: vaciarEdicionesP2,
    reponer: reponerEdicionesP2,
  } = useEdicionesP2()
  const normalizedResults = useMemo(
    () => aplicarEdiciones(normalizedResultsMotor, edicionesP2),
    [normalizedResultsMotor, edicionesP2],
  )
  const geocodedResults = useMemo(
    () => aplicarEdiciones(geocodedResultsMotor, edicionesP2),
    [geocodedResultsMotor, edicionesP2],
  )

  // BUG-7: id de la infraestructura que se está editando manualmente (null = ninguna)
  const [editingInfraId, setEditingInfraId] = useState<string | null>(null)

  const handleOpenManualEdit = useCallback((infraId: string) => {
    setEditingInfraId(infraId)
  }, [])

  const handleSaveEdit = useCallback((infraId: string, data: { name: string; address: string }) => {
    patchResult(infraId, { nombre: data.name, direccion: data.address })
    editarFilaP2(infraId, { nombre: data.name, direccion: data.address })
    setEditingInfraId(null)
  }, [patchResult, editarFilaP2])

  // Lote C1: edición manual de coordenadas UTM30 (faldón del mapa → «Edición manual»).
  // Fuente única de verdad: ProcessedInfrastructure.xFinal/yFinal — el mismo par que
  // C1b (β, pin arrastrable) leerá/escribirá sin reescritura.
  const [editingCoordsInfraId, setEditingCoordsInfraId] = useState<string | null>(null)

  const handleEditCoords = useCallback((infraId: string) => {
    setEditingCoordsInfraId(infraId)
  }, [])

  // Lote C2: el cambio se refleja en la UI de inmediato (optimista) y se persiste
  // al Worker /coord-edits en segundo plano. Un fallo solo emite un toast — nunca
  // bloquea ni revierte la edición ya visible. Sumidero único: cubre la edición
  // manual (onApply, source='manual') y el arrastre del pin (onDragEditCoords, source='map').
  const handleApplyCoords = useCallback((
    infraId: string,
    x: number,
    y: number,
    // S1-5a: 'assisted' = aplicar candidato de geocodificación (DOP-S1-5A-CANAL=a)
    source: 'manual' | 'map' | 'assisted' = 'manual',
  ) => {
    // S1-4a: `prev` lee de la misma cascada que el display (geo > norm) para que
    // el geomBefore del historial refleje lo que el usuario tenía delante.
    const fuentePrev = geocodedResults.length > 0 ? geocodedResults : normalizedResults
    const prev = fuentePrev.find(r => r.id === infraId)
    const ine5 = extractionData?.metadata.ineCode ?? ''

    // Optimista: aplicar al estado del wizard antes de persistir.
    // S1-4a · doble-patch (OBS-P2-PATCH-C2-FUENTE-GEOCODIFICADA): el display lee
    // la cascada geo > norm — se parchean AMBAS fuentes (DOP-γ 12-jul: el patch
    // sobre la fuente inactiva es un no-op inocuo). Patrón calcado de
    // handleConfirmElement.
    patchResult(infraId, { xFinal: x, yFinal: y })
    patchGeocodedResult(infraId, { xFinal: x, yFinal: y })
    editarFilaP2(infraId, { xFinal: x, yFinal: y })
    setEditingCoordsInfraId(null)

    if (!user) {
      toast.error('Debes iniciar sesión para guardar la corrección de coordenadas')
      return
    }

    const payload = buildCoordEditPayload({ infraId, x, y, source, ine5, prev })

    void (async () => {
      try {
        const idToken = await user.getIdToken()
        await postCoordEdit(payload, idToken)
      } catch (err) {
        // SEC0-c1: sin permiso → aviso claro, sin código técnico.
        if (err instanceof CoordEditError && err.message === MENSAJE_SIN_PERMISO) {
          toast.error(MENSAJE_SIN_PERMISO)
          return
        }
        const status = err instanceof CoordEditError ? ` (${err.status})` : ''
        toast.error(`No se pudo guardar la corrección de coordenadas${status}`)
      }
    })()
  }, [patchResult, patchGeocodedResult, editarFilaP2, geocodedResults, normalizedResults, extractionData, user])

  // C2: el arrastre del pin marca origen 'map'; la edición manual usa el default 'manual'.
  const handleDragCoords = useCallback(
    (infraId: string, x: number, y: number) => handleApplyCoords(infraId, x, y, 'map'),
    [handleApplyCoords],
  )

  // P2-S3: «Confirmar elemento» — validación humana del pie de ficha (AUDIT
  // modelo B §7.3). Lee de la misma cascada que el display (geo > norm) y
  // parchea AMBAS fuentes (DOP-γ 12-jul: el patch sobre la fuente inactiva es
  // un no-op inocuo), garantizando que la fila sale de «Sin geolocalizar» y el
  // progreso avanza sea cual sea la fuente activa.
  const handleConfirmElement = useCallback((infraId: string) => {
    const fuente = geocodedResults.length > 0 ? geocodedResults : normalizedResults
    const infra = fuente.find(r => r.id === infraId)
    if (!infra) {
      toast.error('No se encontró el elemento a confirmar')
      return
    }

    // DOP-S3-3=a: recálculo de score por elemento vía revalidateRecord
    // (D1 manual/L0 + D2 exact + D3 espacial). Primer consumidor productivo.
    const resultado = revalidateRecord({
      x: infra.xFinal ?? null,
      y: infra.yFinal ?? null,
      codIne: extractionData?.metadata.ineCode ?? '',
      municipio: infra.municipio ?? '',
      score: infra.score ?? 0,
    })

    // HX-FALLBACK-ENMASCARA-BUG: un fallo de validación NO confirma en silencio.
    if (!resultado.success) {
      toast.error(`No se puede confirmar: ${resultado.message}`, {
        description: resultado.recomendaciones[0],
      })
      return
    }

    // DOP-S3-2=a · invariante §6: `confirmado`/`confirmedAt` se guardan JUNTO a
    // los valores existentes; nada se sobrescribe salvo el score recalculado.
    const patch = {
      confirmado: true,
      confirmedAt: new Date().toISOString(),
      score: resultado.newScore,
      scoreDetail: resultado.scoreDetail,
    }
    patchResult(infraId, patch)
    patchGeocodedResult(infraId, patch)
    editarFilaP2(infraId, patch)
    toast.success(`Elemento confirmado (score ${resultado.newScore})`)

    // Persistencia PostGIS (Worker /coord-edits) — patrón C2/GIS-D04, fire-and-
    // forget: un fallo emite toast, nunca revierte la confirmación local.
    if (!user) {
      toast.warning('Confirmación aplicada localmente — inicia sesión para persistirla')
      return
    }
    if (infra.xFinal == null || infra.yFinal == null) return // inalcanzable (success ⇒ coords); guardia TS
    const payload: CoordEditPayload = {
      infraId,
      infraNombre: infra.nombre ?? '',
      ine5: extractionData?.metadata.ineCode ?? '',
      // Confirmación pura: las coordenadas no cambian (before === after);
      // el registro documenta la validación humana (editSource 'assisted').
      geomBefore: { x: infra.xFinal, y: infra.yFinal },
      geomAfter: { x: infra.xFinal, y: infra.yFinal },
      editSource: 'assisted',
    }
    void (async () => {
      try {
        const idToken = await user.getIdToken()
        await postCoordEdit(payload, idToken)
      } catch (err) {
        if (err instanceof CoordEditError && err.message === MENSAJE_SIN_PERMISO) {
          toast.error(MENSAJE_SIN_PERMISO)
          return
        }
        const status = err instanceof CoordEditError ? ` (${err.status})` : ''
        toast.error(`No se pudo persistir la confirmación${status}`)
      }
    })()
  }, [geocodedResults, normalizedResults, extractionData, patchResult, patchGeocodedResult, editarFilaP2, user])

  const hasData = extractionData !== null
  // BEF1: P2 (Valida) es vista map-céntrica → necesita una cadena de altura
  // DEFINIDA (100dvh + flex-column) para que el footer (StatusBar en la ranura
  // `barraEstado` del WorkspaceShell) entre en el viewport. Con `minHeight:100vh`
  // (bloque) el `height:100%` del shell se resolvía contra altura indefinida →
  // el grid crecía a contenido y empujaba el footer bajo el fold. P1 y P3
  // conservan el flujo de documento (bloque), sin regresión.
  const esVistaP2Mapa = currentStep === 2
  // B20: banner de sesión expirada — dismissable una vez por montaje
  const [sessionBannerDismissed, setSessionBannerDismissed] = useState(false)
  const showSessionExpiredBanner = sessionExpired && !user && !sessionBannerDismissed
  const { canValidate } = usePermissions()   // U.01: guardia de rol para pipeline auto
  const { logDocument } = useAudit()

  // === COTEJO DE COMPLETITUD (P3) ===
  const {
    resultado: cotejoResult,
    loading: cotejoLoading,
    error: cotejoError,
    ejecutar: ejecutarCotejo,
    reset: resetCotejo,
  } = useCotejo()

  // C0-b: lo que decide el técnico en P3 vive aquí, por id, y sobrevive a salir
  // de P3. Se vacía con el documento (resetEstadoDerivadoDelDocumento). C0-e2:
  // entra en la caché de sesión y «Recuperar» lo repone.
  const {
    decisiones: decisionesFila,
    decidir: decidirFila,
    vaciar: vaciarDecisionesFila,
    reponer: reponerDecisionesFila,
  } = useDecisionesFila(user?.uid ?? null)
  // Ítems de P3 estables mientras no cambie el cotejo (antes se rehacían en cada render).
  const itemsCotejoP3 = useMemo(
    () => (cotejoResult ? resultadoToCotejoItems(cotejoResult) : []),
    [cotejoResult],
  )
  // C0-d: P4 recibe el cotejo con lo decidido en P3 (DOP-4 b); P3, su cabecera
  // y el registro del documento siguen con el cotejo del motor.
  const cotejoParaP4 = useMemo(
    () => (cotejoResult ? aplicarDecisionesAlResultado(cotejoResult, decisionesFila) : undefined),
    [cotejoResult, decisionesFila],
  )

  useEffect(() => {
    if (!locationState?.file && hasCache) {
      const cached = restoreState()
      if (cached && cached.currentStep > 1) {
        cachedStateRef.current = cached
        setShowRecoveryBanner(true)
        setCachedExtractionData(cached.extractionData)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: hasCache/locationState/restoreState estables al montar
  }, [])

  useEffect(() => {
    if (locationState?.file) {
      handleFileSelect(locationState.file)
      window.history.replaceState({}, document.title)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleFileSelect es useCallback estable; solo reejecutar cuando cambia locationState
  }, [locationState])

  // BUG-UI-02: guard de ruta — si la URL apunta a un paso avanzado sin datos ni caché, redirigir a P1
  useEffect(() => {
    if (initialStep > 1 && !extractionData && !hasCache) {
      setCurrentStep(1)
      navigate('/wizard/1', { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: valores estables al montar
  }, [])

  // === EFECTO: Normalizar después de extracción ===
  // RECEXT (DOP-1 b′): la normalización solo arranca en P1/P2. «Recuperar»
  // repone extractionData también en P3/P4 (cabecera, INE del cotejo, caché),
  // donde la pantalla vive de processedData y normalizedResults está vacío:
  // sin esta guarda se relanzaría normalización + geocodificación en segundo
  // plano (lo que CACHREC DOP-4b quiso evitar). Si el usuario vuelve a P2, el
  // pipeline corre como en una carga normal.
  useEffect(() => {
    if (currentStep > 2) return
    if (extractionData && extractionData.infrastructures.length > 0 && !isNormalizing && normalizedResults.length === 0) {
      console.log('[WizardPageV2] 📍 Iniciando normalización de', extractionData.infrastructures.length, 'infraestructuras')
      normalizeAll(extractionData.infrastructures, extractionData.metadata.ineCode)
    }
  }, [currentStep, extractionData, isNormalizing, normalizedResults.length, normalizeAll])

  // === EFECTO: Activar animación flecha/stepper SOLO al terminar normalización ===
  // Punto 8 UI: la flecha ">" y el highlight del paso 2 solo aparecen cuando
  // el proceso está completo al 100%, no durante la normalización.
  //
  // OBS-FN-02-A: este efecto antes también auditaba `normalization_complete`
  // a Firestore FB.08, pero reportaba cobertura pre-geocoding (6/60 en
  // Colomera) que era engañosa. La auditoría se ha movido al efecto posterior
  // que dispara tras `geoStats` disponible (ver más abajo), de modo que mida
  // cobertura final post-pipeline.
  useEffect(() => {
    if (!isNormalizing && normalizedResults.length > 0 && justCompletedStep === null && completedSteps.includes(1)) {
      setJustCompletedStep(1)
    }
  }, [isNormalizing, normalizedResults, justCompletedStep, completedSteps])

  // === EFECTO: Geocodificar después de normalización ===
  useEffect(() => {
    // U.01 FIX: La geocodificación por dirección (CartoCiudad/CDAU/NGA) es necesaria
    // para TODOS los usuarios autenticados cuando el documento no trae coordenadas
    // (caso Colomera: X/Y = "Indicar" o vacíos en Tabla60/Tabla61).
    // Solo se bloquea si el usuario no está autenticado en absoluto.
    if (!user) return

    // Condiciones de salida temprana
    if (isNormalizing || isGeocoding) return
    // P1PROV-G1 (DOP-1 a): con documento y estadísticas, la tanda de este
    // documento ya terminó (CAMDOC las vacía al cambiar de documento) → P1
    // completado.
    if (geoStats) {
      if (extractionData) marcarP1Completado()
      return
    }
    if (normalizedResults.length === 0 || !extractionData) return

    // TODOVAL (DOP-1 a, DOP-2 a): la geolocalización con validación cruzada
    // corre SIEMPRE, también si todas las filas traen coordenada. Antes solo
    // arrancaba si alguna fila tenía nota 0 o le faltaba X/Y: 19 de 51 planes
    // del corpus (285 filas, El Cuervo 33/33) salían con la nota de lectura
    // (95) sin contrastar con ninguna fuente (H1, 29-oct). Con coordenada del
    // documento rigen las reglas de siempre (≤25 m validada, 100-500 m
    // sustituida, >500 m revisión manual conservando la del documento).
    console.log('[WizardPageV2] 🌍 Iniciando geolocalización y validación cruzada de', normalizedResults.length, 'infraestructuras')
    // P1PROV-G1 (DOP-1 a, DOP-2 a): la promesa se resuelve siempre (fin de
    // tanda, error global o tanda descartada); P1 se completa solo si el
    // documento sigue siendo el mismo que la lanzó.
    const documento = extractionData
    void Promise.resolve(geocodeNeeded(normalizedResults, extractionData.metadata)).then(() => {
      if (isMounted() && extractionDataRef.current === documento) marcarP1Completado()
    })
  }, [normalizedResults, isNormalizing, isGeocoding, geoStats, geocodeNeeded, extractionData, canValidate, isMounted, marcarP1Completado])

  // === EFECTO FB.08-STEP2: Auditar geocodificación completada ===
  useEffect(() => {
    if (!geoStats) return
    logDocument(
      'update',
      extractionData?.metadata.ineCode || 'unknown',
      extractionData?.metadata.ineCode || '',
      null,
      {
        event: 'geocoding_complete',
        total: geoStats.total,
        success: geoStats.success,
        failed: geoStats.failed,
        averageScore: geoStats.averageScore,
        processingTimeMs: geoStats.processingTime,
      },
    ).catch(() => { /* auditoría no bloquea el flujo */ })
  }, [geoStats, logDocument, extractionData])

  // === EFECTO FB.08-STEP1-ODT (OBS-FN-02-A): Auditar normalización completada ===
  // Dispara tras `geoStats` disponible (fin del pipeline P1 completo) y mide
  // cobertura real sobre la cascada geo > norm > raw. Antes disparaba tras
  // `normalizeAll` y reportaba 6/60 en Colomera cuando el motor había
  // rescatado 54. El nombre del evento se mantiene para no romper dashboards.
  useEffect(() => {
    if (!geoStats) return
    const picked = pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData)
    const resolvedCount = countResolved(picked.data)
    const totalInfras = picked.data.length
    const withoutCoords = totalInfras - resolvedCount
    // avgScore sobre filas resueltas (score > 0 garantizado por isResolved=true
    // con coords válidas, pero REVISION_MANUAL tiene score 30 — se incluye).
    const resolved = picked.data.filter(r =>
      r.status === 'original' || r.status === 'geocoded' || r.status === 'manual',
    )
    const avgScore = resolved.length > 0
      ? Math.round(resolved.reduce((acc, r) => acc + (r.score ?? 0), 0) / resolved.length)
      : 0
    logDocument(
      'update',
      extractionData?.metadata.ineCode || 'unknown',
      extractionData?.metadata.ineCode || '',
      null,
      {
        event: 'normalization_complete',
        totalInfras,
        withCoords: resolvedCount,
        withoutCoords,
        avgScore,
        // OBS-FN-02-A: campos añadidos para trazabilidad del cambio. Permiten
        // agregar dashboards sobre la cobertura real del pipeline y detectar
        // regresiones de la propagación geo→UI.
        displaySource: picked.source,
      },
    ).catch(() => { /* auditoría no bloquea el flujo */ })
  }, [geoStats, normalizedResults, geocodedResults, extractionData, logDocument])

  // === EFECTO GUIA-DEVOLUCION-02: Detectar errores graves (banner ámbar) ===
  // Se activa tras finalizar la geocodificación (geoStats disponible).
  // SEC0-b2b DOP-11 (a): se retiró el envío a `/api/send-rejection-feedback`
  // (ruta inexistente en Pages y retirada del Worker); el aviso al
  // ayuntamiento queda para `FORM0`.
  useEffect(() => {
    if (!geoStats || geocodedResults.length === 0) return
    // OBS-P2-REJECTION-BANNER-PCT-SOSPECHOSO: no marcar como error las filas que
    // el pipeline ya resolvió (p. ej. yOriginal vacía pero geocodificada por
    // dirección). El denominador sigue siendo el total → % fiel sobre lo atascado.
    const summary = detectSevereErrors(geocodedResults, { skip: isResolved })
    setSevereErrorSummary(summary)
  }, [geoStats, geocodedResults])

  // === EFECTO: Ejecutar cotejo al entrar en step 3 ===
  // C0-c (DOP-3 a): el cotejo se rehace cuando cambia lo que el emparejamiento
  // lee de las filas (huella), no solo al cambiar de documento. Antes exigía
  // `!cotejoResult`: corregir en P2 tras pasar por P3 dejaba el cotejo viejo en
  // P3 y en el informe de P4. Lo decidido en P3 va por id (`useDecisionesFila`)
  // y sobrevive a rehacerlo. Si un cotejo viejo termina mientras tanto, la
  // huella ya no coincide y se vuelve a lanzar. En P4 (camino C: se llega por
  // la pastilla desde P2) solo se rehace si ya hubo un cotejo; tras «Recuperar»
  // en P4 no se lanza, como antes.
  const huellaFilasP3 = useMemo(
    () => (processedData ? huellaCotejo(processedData.infrastructures) : null),
    [processedData],
  )
  const huellaCotejadaRef = useRef<string | null>(null)
  useEffect(() => {
    const tocaCotejar = currentStep === 3 || (currentStep === 4 && huellaCotejadaRef.current !== null)
    if (!tocaCotejar || !processedData || huellaFilasP3 === null || cotejoLoading) return
    const vigente = huellaCotejadaRef.current === huellaFilasP3
    if (vigente && (cotejoResult || cotejoError)) return
    const ine5 = extractionData?.metadata.ineCode ?? ''
    const municipio = extractionData?.metadata.municipality ?? ''
    if (!ine5) return
    if (cotejoResult || cotejoError) resetCotejo()
    huellaCotejadaRef.current = huellaFilasP3
    console.log('[WizardPageV2] 🔍 Iniciando cotejo completitud para', municipio, '(' + ine5 + ')')
    ejecutarCotejo(processedData.infrastructures, ine5, municipio)
  }, [currentStep, processedData, huellaFilasP3, cotejoResult, cotejoLoading, cotejoError, extractionData, ejecutarCotejo, resetCotejo])

  // ALBRTA-b1: filas del plan para «Alojamientos que el plan no recoge» de P3. Mismo filtro
  // y misma conversión que P4 (`useExportWizard`), para que las cifras del plan coincidan.
  // La sección es un añadido: si la conversión falla, P3 sigue sin ella (DOP-6, no bloquea el cotejo).
  const registrosPlanP3 = useMemo(() => {
    if (currentStep !== 3 || !processedData) return undefined
    try {
      return toProcessedRecords(processedData.infrastructures.filter(i => !i.esPlaceholder))
    } catch (error) {
      console.warn('[ALBRTA-b1] sin sección de alojamientos: no se pudieron convertir las filas del plan', error)
      return undefined
    }
  }, [currentStep, processedData])

  // === EFECTO: Marcar step 3 completado cuando cotejo termina ===
  useEffect(() => {
    if (cotejoResult && !completedSteps.includes(3)) {
      setCompletedSteps(prev => [...prev, 3 as StepId])
    }
  }, [cotejoResult, completedSteps])

  // === EFECTO FB.08-STEP3: Auditar cotejo completado ===
  useEffect(() => {
    if (!cotejoResult) return
    const st = cotejoResult.estadisticas
    logDocument(
      'update',
      extractionData?.metadata.ineCode || 'unknown',
      extractionData?.metadata.ineCode || '',
      null,
      {
        event: 'cotejo_complete',
        totalPTEL: st.totalPTEL,
        totalReferencia: st.totalReferencia,
        incluidos: st.incluidos,
        probables: st.probables,
        ausentesCriticos: st.ausentesCriticos,
        ausentesRecomendados: st.ausentesRecomendados,
        sobrantes: st.sobrantes,
        ratioCoberturaGlobal: Math.round(st.ratioCoberturaGlobal * 100),
      },
    ).catch(() => { /* auditoría no bloquea el flujo */ })
  }, [cotejoResult, logDocument, extractionData])

  const handleRestoreFromCache = useCallback(() => {
    // RECEXT (DOP-3 a): se restaura lo que leyó el banner, no una relectura.
    const cached = cachedStateRef.current
    if (cached) {
      setCurrentStep(cached.currentStep as 1 | 2 | 3 | 4)
      setCompletedSteps(cached.completedSteps as StepId[])
      if (cached.processedData) {
        setProcessedData(cached.processedData as ProcessingComplete)
      }
      if (cached.extractionData) {
        // CACHREC (DOP-4b a) reponía extractionData solo sin processedData
        // (sesión interrumpida en P2). RECEXT: se repone siempre. Sin él, P3
        // quedaba en blanco (el cotejo toma el INE de aquí), la cabecera de
        // documento desaparecía y el efecto de guardado reescribía la caché
        // con `extractionData: null`, con lo que una segunda recarga ya no
        // ofrecía recuperar (medido en producción 18-sep-2026,
        // .ptel/p0_recext_20260918/). El pipeline no se relanza en P3/P4 por
        // la guarda de paso del efecto de normalización (DOP-1 b′).
        setExtractionData(fromSerializable(cached.extractionData))
      }
      // C0-e2 (DOP-3 a): lo decidido en P3 vuelve validado entrada a entrada.
      reponerDecisionesFila(cached.decisiones)
      // C0-e3 (DOP-2 a): lo corregido en P2 vuelve validado campo a campo.
      reponerEdicionesP2(cached.ediciones)
      setShowRecoveryBanner(false)
    }
  }, [fromSerializable, reponerDecisionesFila, reponerEdicionesP2])

  const handleDiscardCache = useCallback(() => {
    clearCache()
    cachedStateRef.current = null
    setShowRecoveryBanner(false)
    setCachedExtractionData(null)
  }, [clearCache])

  useEffect(() => {
    // CACHREC (DOP-1 a): sin extractionData ni processedData no hay nada que
    // guardar. Es el único caso en que el montaje escribe: al recargar en
    // `#/wizard/N` (N > 1) initialStep viene de la URL y este efecto corría
    // con los datos aún nulos, pisando la caché que el banner acababa de leer
    // y que «Recuperar» relee (medido en producción 18-sep-2026,
    // .ptel/p0_cachrec_20260918/AUDIT_CACHREC.md §3.1).
    if (!extractionData && !processedData) return
    if (currentStep > 1 || completedSteps.length > 0 || extractionData) {
      saveState({
        currentStep,
        completedSteps,
        extractionData: extractionData ? toSerializable(extractionData) : null,
        processedData,
        // C0-e2 (DOP-2 a): una decisión en P3 sin cambiar de paso también se guarda.
        decisiones: decisionesFila,
        // C0-e3: una corrección en P2 sin cambiar de paso también se guarda.
        ediciones: edicionesP2
      })
    }
  }, [currentStep, completedSteps, extractionData, processedData, decisionesFila, edicionesP2, saveState, toSerializable])

  // P1-S3: continuación del pipeline una vez garantizado el INE del municipio.
  // Compartida por el flujo normal (handleFileSelect) y por la confirmación
  // manual de municipio (handleConfirmMunicipio) — evita re-parsear el
  // documento y mantiene idéntico el comportamiento del camino feliz.
  const finalizeExtraction = (
    file: File,
    extracted: Awaited<ReturnType<typeof extractInfrastructures>>,
  ) => {
    const { infrastructures, stats, metadata } = extracted

    setProcessingStatus('Analizando formato...')
    const sampleText = infrastructures
      .slice(0, 10)
      .map(i => `${i.nombre || ''} ${i.direccion || ''} ${i.xOriginal || ''} ${i.yOriginal || ''}`)
      .join('\n')
    const formatAnalysis = detectFormat(sampleText, file.name)

    setProcessingStatus('¡Extracción completada!')

    // C1: Advertir si el INE5 ya fue cargado en esta sesión
    const ine5 = metadata.ineCode ?? ''
    if (isIne5Duplicate(sessionIne5s, ine5)) {
      toast.warning(`Municipio duplicado (INE5: ${ine5})`, {
        description: 'Ya procesaste un documento con este código INE en la sesión actual. Se reemplazará por el nuevo.',
        duration: 7000,
      })
    }
    setSessionIne5s(prev => addIne5ToSession(prev, ine5))

    // VALMUNI-b (DOP-1 a): con el INE garantizado se pide ya el contorno del
    // municipio, sin esperar. Llega mientras se normaliza; la geolocalización lo
    // espera con tope (`useAutoGeocoding`). Sirve también a los documentos que
    // no pasan por la geolocalización (mapa del informe). No lanza.
    void precargarContorno(ine5)

    const result: ExtractionResult = {
      file,
      infrastructures,
      stats,
      metadata,
      formatAnalysis
    }

    setExtractionData(result)

    // INFDOC-3a: la huella llega después; si falla, el informe dirá que no la tiene
    calculateBlobSHA256(file)
      .then(sha256 => { if (isMounted()) setHuellaDocumento({ file, sha256 }) })
      .catch(err => console.warn('[INFDOC-3a] No se pudo calcular la huella del documento:', err))

    // FB.08: Auditar carga de documento
    logDocument('create', file.name, metadata.ineCode || '', null, {
      fileName: file.name,
      fileSize: file.size,
      infraCount: infrastructures.length,
      format: formatAnalysis?.format ?? 'unknown',
    }).catch(() => { /* auditoría no bloquea el flujo */ })

    // P1PROV-G1 (DOP-1 a): P1 ya no se marca completado al extraer; lo marca
    // el efecto de geolocalizar al terminar el pipeline (`marcarP1Completado`).
  }

  // CAMDOC (OBS-P1-CAMBIO-DE-DOCUMENTO-NO-REEMPLAZA-RESULTADOS): todo lo que
  // se deriva del documento cargado. Sin esto, «Cancelar documento» + otro ODT
  // cambiaba la cabecera pero la tabla, P2, el cotejo y `processedData` (lo que
  // exporta P4) seguían siendo del municipio anterior: el efecto de normalizar
  // exige `normalizedResults.length === 0` y el de geocodificar sale si hay
  // `geoStats`, así que con el estado viejo el documento nuevo nunca se procesaba.
  // Los `reset()` de los hooks invalidan además la tanda en vuelo.
  const resetEstadoDerivadoDelDocumento = () => {
    resetNormalization()
    resetGeocoding()
    resetCotejo()
    huellaCotejadaRef.current = null
    vaciarDecisionesFila()
    vaciarEdicionesP2()
    setProcessedData(null)
    setCompletedSteps([])
    setJustCompletedStep(null)
    setSevereErrorSummary(null)
    setEditingInfraId(null)
    setEditingCoordsInfraId(null)
  }

  const handleFileSelect = async (file: File) => {
    // Auth gate: bloquear carga si usuario no autenticado
    if (!user) {
      toast.warning('Registro necesario', {
        description: 'Regístrate o inicia sesión para procesar documentos PTEL.',
        duration: 6000,
      })
      return
    }

    // Escenario 1 — rechazo in situ con remedio diferenciado. Validaciones
    // síncronas previas al trabajo pesado: no arrancan el spinner.
    setProcessingError(null)
    setZeroInfraDiagnostic(null)
    setMunicipioPendiente(null)

    const extension = file.name.split('.').pop()?.toLowerCase()
    if (!isAcceptedExtension(extension)) {
      setProcessingError(
        `No puedo leer «.${extension ?? ''}». Formatos aceptados: ${ACCEPTED_FORMATS_LABEL}.`,
      )
      return
    }
    if (file.size === 0) {
      setProcessingError('El archivo parece estar dañado o vacío.')
      return
    }

    // CAMDOC (DOP-1 c): el documento nuevo reemplaza siempre, venga de donde
    // venga el estado previo.
    resetEstadoDerivadoDelDocumento()

    setIsProcessing(true)
    setProcessingStatus('Leyendo documento...')

    try {
      setProcessingStatus('Extrayendo infraestructuras...')
      const { infrastructures, stats, metadata } = await extractInfrastructures(file)

      // SSCA-FIX-1: guard de montaje - extractInfrastructures puede
      // settlearse tras el desmontaje del componente; sin guard, los
      // setState siguientes agendarian trabajo sobre un componente muerto.
      if (isMounted()) {
        if (infrastructures.length === 0) {
          // P1-S2 (esc. 3): documento válido con 0 infraestructuras reconocidas.
          // Se conserva el diagnóstico del extractor (formato + municipio/INE
          // detectados del nombre) en lugar de lanzar un error mudo. No se
          // asigna extractionData: ni normalización ni paso completado.
          setZeroInfraDiagnostic(adaptZeroInfraDiagnostic(file.name, metadata))
          return
        }

        // P1-S3 (esc. 4): municipio no detectado o ambiguo — sin INE no hay
        // cascada de geolocalización ni validación fiables → GATE bloqueante
        // (decisión 4a). No se asigna extractionData: ni normalización ni
        // paso completado hasta que un usuario con permiso confirme (4b).
        if (!metadata.ineCode) {
          setMunicipioPendiente({
            file,
            extracted: { infrastructures, stats, metadata },
            diagnostic: adaptMunicipioPendiente(file.name, metadata),
          })
          return
        }

        finalizeExtraction(file, { infrastructures, stats, metadata })
      }
    } catch (err) {
      console.error('Error procesando archivo:', err)
      if (isMounted()) {
        // P1-S2: el caso 0-infra ya no llega aquí (rama propia con diagnóstico);
        // cualquier excepción de parseo se reporta como dañado o vacío.
        setProcessingError('El archivo parece estar dañado o vacío.')
      }
    } finally {
      if (isMounted()) {
        setIsProcessing(false)
        setProcessingStatus('')
      }
    }
  }

  const handleFileReset = () => {
    resetEstadoDerivadoDelDocumento()
    clearCache()
    setExtractionData(null)
    setProcessingError(null)
    setZeroInfraDiagnostic(null)
    setMunicipioPendiente(null)
    setCurrentStep(1)
    navigate('/wizard/1', { replace: true })
  }

  // FILAPASOS (DOP-3b a): «Cancelar documento» desde la tira (P1 panel /
  // P2-P4 tira) y desde los diagnósticos sin datos de Step1Page: reset + aviso.
  // El toast vivía en el botón de la ranura `actions` de la fila de pasos.
  const handleCancelarDocumento = () => {
    handleFileReset()
    toast.info('Archivo eliminado', {
      description: 'Puedes cargar un nuevo documento',
      duration: 2500,
    })
  }

  // P1-S3 (esc. 4): confirmación manual del municipio (decisión 4b).
  // Solo usuario con permiso (canValidate = editor+). El catálogo INE
  // maestro es INMUTABLE: la confirmación se guarda como metadato del
  // trabajo (metadata del wizard, serializada en la caché de sesión) con
  // traza auditada en Firestore vía logDocument (quién/cuándo/qué INE).
  const handleConfirmMunicipio = (codigo: string) => {
    const pendiente = municipioPendiente
    if (!pendiente) return
    if (!canValidate) {
      toast.warning('Permiso insuficiente', {
        description: 'Necesitas permiso de edición para confirmar el municipio.',
        duration: 6000,
      })
      return
    }
    const muni = getMunicipioPorCodigo(codigo)
    if (!muni) return // fuera del catálogo de los 785: no confirmar

    const metadata = {
      ...pendiente.extracted.metadata,
      municipality: muni.nombre,
      province: muni.provincia,
      ineCode: muni.codigo,
    }

    // Traza mínima: quién (uid/email los añade audit.service) / cuándo
    // (timestamp del servicio) / qué INE (before → after).
    logDocument('update', pendiente.file.name, muni.codigo,
      { municipality: pendiente.extracted.metadata.municipality ?? null, ineCode: null },
      { municipality: muni.nombre, ineCode: muni.codigo, source: 'confirmacion-manual-municipio' },
    ).catch(() => { /* auditoría no bloquea el flujo */ })

    setMunicipioPendiente(null)
    finalizeExtraction(pendiente.file, { ...pendiente.extracted, metadata })
    setProcessingStatus('')
    toast.success(`Municipio confirmado: ${muni.nombre} (${muni.provincia})`, {
      description: `INE ${muni.codigo}`,
      duration: 5000,
    })
  }

  // P4IDENT (DOP-3 c): aquí es donde fila y cabecera se juntan. Si alguna
  // fila no es del documento activo, no se construye `processedData` (ni
  // P3 cotejaría ni P4 exportaría un estado mezclado). No debería ocurrir
  // tras CAMDOC; es defensa en profundidad. C0-c: lo usan la flecha y las
  // pastillas del cabecero al salir de P2 hacia delante.
  const fijarProcessedData = (data: ProcessingComplete): boolean => {
    const pertenencia = filasPertenecenAlDocumento(data.infrastructures, extractionData?.infrastructures)
    if (!pertenencia.pertenecen) {
      console.error('[P4IDENT] filas ajenas al documento activo en P2 → P3', pertenencia)
      toast.error('Las infraestructuras no pertenecen al documento activo', {
        description: mensajeFilasAjenas(extractionData?.file.name),
        duration: 8000,
      })
      return false
    }
    setProcessedData(data)
    return true
  }

  const handleStep2Complete = (data: ProcessingComplete) => {
    if (!fijarProcessedData(data)) return
    if (!completedSteps.includes(2)) {
      setCompletedSteps([...completedSteps, 2])
    }

    // FB.08: Auditar validación completada en P2
    const ine = extractionData?.metadata.ineCode ?? ''
    logDocument('update', extractionData?.file.name ?? 'unknown', ine, null, {
      step: 'valida-completada',
      infraCount: data.infrastructures.length,
      geoCount: data.infrastructures.filter(i => (i.score ?? 0) > 0).length,
    }).catch(() => { /* auditoría no bloquea el flujo */ })

    // Navegar a P3 Coteja (ya no a Descarga directamente)
    setCurrentStep(3)
    navigate('/wizard/3', { replace: true })
  }

  const handleStepClick = (step: number) => {
    if (step <= Math.max(...completedSteps, 0) + 1) {
      // C0-c (camino C, Luis 24-oct): salir de P2 hacia delante por la pastilla
      // rehace la foto igual que la flecha. Solo con resultados del pipeline
      // (como `canAdvance`): tras «Recuperar» y volver a P2, mientras se
      // rehace la geolocalización, se conserva la foto recuperada.
      if (currentStep === 2 && step > 2 && extractionData
        && (geocodedResults.length > 0 || normalizedResults.length > 0)) {
        const datos = construirProcessedData(
          pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData).data,
          extractionData.metadata,
        )
        if (!fijarProcessedData(datos)) return
      }
      setCurrentStep(step as 1 | 2 | 3 | 4)
      navigate(`/wizard/${step}`, { replace: true })
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      const newStep = (currentStep - 1) as 1 | 2 | 3 | 4
      setCurrentStep(newStep)
      navigate(`/wizard/${newStep}`, { replace: true })
    }
  }

  const handleNext = () => {
    if (currentStep < 4 && completedSteps.includes(currentStep)) {
      const newStep = (currentStep + 1) as 1 | 2 | 3 | 4
      setCurrentStep(newStep)
      navigate(`/wizard/${newStep}`, { replace: true })
    }
  }

  const _getMetrics = () => {
    if (!extractionData) return undefined
    
    const { stats } = extractionData
    switch (currentStep) {
      case 1:
        return { current: stats.withCoordinates, total: stats.total, label: 'geolocalizados' }
      case 2:
        return { current: stats.withCoordinates, total: stats.total, label: 'validados' }
      case 3:
        return { current: cotejoResult?.estadisticas.incluidos ?? 0, total: cotejoResult?.estadisticas.totalReferencia ?? stats.total, label: 'cotejados' }
      case 4:
        return { current: stats.total, total: stats.total, label: 'para exportar' }
      default:
        return undefined
    }
  }

  // OBS-FN-02-A · fuente de la tabla P1:
  // Antes: solo `normalizedResults` → mostraba 6/60 en Colomera porque el
  // cascade del orchestrator (useAutoGeocoding) depositaba sus resultados en
  // un useState interno que nadie fusionaba con el array pintado.
  // Ahora: cascada explícita geo > norm > raw vía pickInfrasForDisplay.
  // B01-FIX sigue aplicando: si normalizedResults está disponible, se usa
  // ProcessedInfrastructure[] con score real (no heurística 70 provisional).
  // P1PROV-a (DOP-1 a): mientras se geolocaliza, cada fila sale con su
  // resultado al terminar (`pickInfrasForP1`); el resto del wizard sigue con
  // `pickInfrasForDisplay` y solo ve resultados finales. Lo terminado viene del
  // motor sin las correcciones de P2: se aplican aquí, como a los dos motores.
  // P1PROV-b2 (DOP-1 a): las filas que siguen en la pasada salen marcadas
  // `pendiente` (la tabla pinta «…» y los filtros no las cuentan, `P1PROV-b1`).
  // Por índice: `pickInfrasForP1`, `aplicarEdiciones` y el adaptador conservan
  // orden y longitud, y el adaptador numera las filas por posición.
  // INFDOC-2a (DOP-2 a): `filasFinales` son esas mismas filas sin adaptar, para
  // el detector de incidencias de P1 (la proyección de la tabla no lo sirve).
  const step1Props = useMemo(() => {
    if (!extractionData) return { documentContext: null, data: [], filasFinales: [] }
    const picked = pickInfrasForP1(normalizedResults, geocodedResults, extractionData, geoParcial)
    if (picked.source === 'geocoded' || picked.source === 'normalized') {
      const filas = aplicarEdiciones(picked.data, edicionesP2)
      const adaptadas = adaptInfrastructures(filas)
      return {
        documentContext: {
          ...adaptMetadata(extractionData.metadata),
          ...(huellaDocumento?.file === extractionData.file ? { huella: huellaDocumento.sha256 } : {}),
        },
        data: picked.pendientes.size === 0
          ? adaptadas
          : adaptadas.map((fila, i) => (picked.pendientes.has(filas[i].id) ? { ...fila, pendiente: true } : fila)),
        filasFinales: filas,
      }
    }
    // source === 'raw' o 'empty': caer al adaptador de ExtractedInfrastructure
    // crudo (heurística 70 transitoria hasta que el pipeline termine).
    return { ...adaptExtractionResult(extractionData), filasFinales: [] }
  }, [extractionData, normalizedResults, geocodedResults, geoParcial, edicionesP2, huellaDocumento])

  // Iconos para badges
  const IconDatabase = () => (
    <svg style={{ width: '12px', height: '12px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  )

  return (
    <div style={{
      minHeight: '100vh',
      ...(esVistaP2Mapa ? { height: '100dvh', display: 'flex' as const, flexDirection: 'column' as const } : null),
      background: '#1E1E1C',
      fontFamily: "'Space Grotesk', system-ui, sans-serif",
    }}>
      {/* DataLoader - silencioso */}
      <DataLoader onReady={() => setDataReady(true)} autoLoad modal />

      {/* Header persistente en AppShell — no renderizar aquí */}

      {/* Spacer para header fixed (~68px con padding 16px + logo 36px + padding 16px) */}
      <div style={{ height: '68px' }} />

      {/* T1CB · Fila 2 de la Cabecera B: los cuatro pasos (desde FILAPASOS,
          solo los pasos). Sustituye al StepperFloating inferior. */}
      <WizardContextBar
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={(step) => {
          setJustCompletedStep(null)
          handleStepClick(step)
        }}
      />

      {/* FILAPASOS (C′): en P2-P4 la identidad del documento + ✕ van en una
          tira de 30 px encima del contenido del paso; en P1 la monta
          DocumentStatusModule como primera línea del panel (vía Step1Page). */}
      {currentStep > 1 && extractionData && (
        <DocumentStrip
          variant="strip"
          documento={{
            fileName: extractionData.metadata.fileName,
            municipality: extractionData.metadata.municipality,
            province: extractionData.metadata.province,
            ineCode: extractionData.metadata.ineCode,
          }}
          onCancel={handleCancelarDocumento}
        />
      )}

      {/* Banner recuperación */}
      <AnimatePresence>
        {showRecoveryBanner && cachedExtractionData && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              background: 'rgba(120, 53, 15, 0.2)',
              borderBottom: '1px solid rgba(180, 83, 9, 0.4)',
            }}
          >
            <div style={{
              maxWidth: '1100px',
              margin: '0 auto',
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ArrowCounterClockwise size={20} style={{ color: '#fbbf24' }} />
                <div style={{ fontSize: '14px' }}>
                  <span style={{ fontWeight: 500, color: '#fde68a' }}>Sesión anterior detectada</span>
                  <span style={{ color: '#fcd34d', marginLeft: '8px' }}>
                    {cachedExtractionData.fileName} ({cachedExtractionData.stats.total} infraestructuras)
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestoreFromCache}
                  className="gap-1.5 text-amber-300 border-amber-700 hover:bg-amber-900/50"
                >
                  <ArrowCounterClockwise size={16} />
                  Recuperar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDiscardCache}
                  className="text-amber-400"
                >
                  <X size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Context Bar eliminada aquí: Step1Page incluye su propio ContextBar
          con métricas, nombre de archivo y botón de cancelación.
          P2/P3/P4 gestionan su propia barra contextual. */}

      {/* BUG-4: GeocodingProgressBar eliminado de nivel wizard.
          El progreso de geocodificación se renderiza dentro de Step1Page
          mediante ProgressBar mode='geocoding'. */}

      {/* Flechas navegación - solo con datos */}
      {hasData && (
        <NavArrows
          currentStep={currentStep}
          canAdvance={
            // BUG-3 FIX: P2 puede avanzar cuando hay resultados del pipeline,
            // sin requerir que processedData ya esté establecido.
            // processedData se construye en onNext (C0-c: siempre).
            currentStep === 2
              ? (geocodedResults.length > 0 || normalizedResults.length > 0)
              : completedSteps.includes(currentStep as StepId)
          }
          animateNext={justCompletedStep === currentStep}
          onPrev={handlePrev}
          onNext={() => {
            setJustCompletedStep(null)
            // BUG-3 FIX · C0-c (DOP-1 b): la flecha de P2 construye processedData
            // SIEMPRE con lo último de P2 (cascada geo > norm > raw), no solo la
            // primera vez. Antes exigía `processedData === null` y, tras pasar
            // por P3, lo corregido en P2 no llegaba ni a P3 ni a P4.
            if (currentStep === 2 && extractionData) {
              handleStep2Complete(construirProcessedData(
                pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData).data,
                extractionData.metadata,
              ))
            } else {
              handleNext()
            }
          }}
        />
      )}

      {/* Contenido principal */}
      <main style={{
        maxWidth: hasData ? 'none' : '1100px',
        margin: hasData ? '0 56px' : '0 auto',
        // T2b: los 96px inferiores eran el hueco del StepperFloating (desmontado en T1CB)
        padding: esVistaP2Mapa ? '24px 0' : hasData ? '24px 0' : '32px 24px',
        ...(esVistaP2Mapa ? { flex: 1, minHeight: 0, display: 'flex' as const, flexDirection: 'column' as const } : null),
      }}>
        {/* Badge servicios - solo en estado vacío */}
        {!hasData && dataReady && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px',
              flexWrap: 'wrap',
            }}
          >
            <ServiceBadge
              label="BBDD Local activa"
              status="online"
              icon={<IconDatabase />}
            />
            {/* Aquí se pueden añadir más badges de servicios online */}
          </motion.div>
        )}

        {/* Título y subtítulo - solo en estado vacío */}
        {!hasData && (
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#f0f0ec',
              marginBottom: '8px',
            }}>
              Normalización de Coordenadas PTEL
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#a8a8a0',
            }}>
              Carga un documento PTEL para extraer y normalizar las coordenadas de infraestructuras
            </p>
          </div>
        )}

        {/* B20 — Banner sesión expirada: solo en Step 1 sin datos cargados */}
        {showSessionExpiredBanner && currentStep === 1 && !hasData && (
          <div style={{ margin: '0 56px 0' }}>
            <InfoBanner
              variant="error"
              visible={true}
              onClose={() => setSessionBannerDismissed(true)}
            >
              <BannerStrong>Tu sesión ha expirado.</BannerStrong>{' '}
              Inicia sesión de nuevo para procesar documentos PTEL.{' '}
              <button
                onClick={() => {
                  // Abrir modal de login — el Header lo gestiona,
                  // emitimos un evento custom que Header escucha
                  window.dispatchEvent(new CustomEvent('ptel:open-login'))
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                  fontWeight: 600,
                }}
              >
                Iniciar sesión →
              </button>
            </InfoBanner>
          </div>
        )}

        {/* Step 1 - Normaliza */}
        {currentStep === 1 && (
          <Step1Page
            documentContext={step1Props.documentContext}
            data={step1Props.data}
            // INFDOC-2a (DOP-2 a): detector de incidencias con las filas finales,
            // el INE real (no el «00000» del adaptador) y el fin de P1 (P1PROV-G1)
            filasFinales={step1Props.filasFinales}
            ineDocumento={extractionData?.metadata.ineCode}
            p1Terminado={completedSteps.includes(1)}
            geolocatedCount={
              // OBS-FN-02-A: contar filas resueltas sobre la cascada completa
              // (geocoded > normalized > raw). Antes usaba solo normalizedResults,
              // que excluía las 48/60 rescatadas por el orchestrator en Colomera.
              // countResolved considera status ∈ {geocoded, manual, original} con
              // xFinal/yFinal finitos — incluye REVISION_MANUAL (score 30 pero
              // coords del doc conservadas).
              countResolved(
                pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData).data,
              ) || (extractionData?.stats.withCoordinates ?? 0)
            }
            totalCount={extractionData?.stats.total ?? 0}
            isProcessing={isProcessing}
            processingStatus={processingStatus}
            processingError={processingError}
            zeroInfraDiagnostic={zeroInfraDiagnostic}
            municipioPendiente={municipioPendiente?.diagnostic ?? null}
            canConfirmMunicipio={canValidate}
            searchMunicipios={buscarCandidatosMunicipio}
            onConfirmMunicipio={handleConfirmMunicipio}
            isNormalizing={isNormalizing}
            normalizationProgress={normalizationProgress}
            isGeocoding={isGeocoding}
            geoProgress={geoProgress}
            onFileSelect={handleFileSelect}
            onFileReset={handleCancelarDocumento}
            onPinClick={(row) => {
              if (row.x && row.y) {
                window.open(`https://www.google.com/maps?q=${row.y},${row.x}`, '_blank')
                toast.info('Abriendo en Google Maps', {
                  description: row.name ?? row.address ?? '',
                  duration: 2500,
                })
              }
            }}
            onCopyCoords={(row) => {
              if (row.x && row.y) {
                navigator.clipboard.writeText(`${row.x}, ${row.y}`)
                toast.success('Coordenadas copiadas', {
                  description: `X ${row.x.toLocaleString('es-ES')} · Y ${row.y.toLocaleString('es-ES')}`,
                  duration: 2000,
                })
              }
            }}
          />
        )}

        {/* Step 2 - Valida (nuevo Step2Page) */}
        {currentStep === 2 && extractionData && (() => {
          // B07-FIX: pasar normalizedResults como segundo fallback.
          // Cuando todas las coords eran originales del documento, geocodedResults
          // queda vacío y normalizedResults es la fuente correcta con score/xFinal/yFinal.
          const step2Props = adaptStep2Props({
            extractionData,
            geocodedResults,
            normalizedResults,
            onOpenManualEdit: handleOpenManualEdit,
            // C0-c: misma construcción que la flecha (antes, bloque duplicado).
            // Step2Page ya no pinta «Continuar» (75f3b0bd); se mantiene el contrato.
            onContinue: () => {
              handleStep2Complete(construirProcessedData(
                pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData).data,
                extractionData.metadata,
              ))
            },
          })
          // A3x-1a: conjunto resuelto (cascada geo>norm>raw) como origen del overview de mapa de P2.
          const allInfras = pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData).data
          return step2Props ? <Step2Page {...step2Props} allInfrastructures={allInfras} onEditCoords={handleEditCoords} onConfirmElement={handleConfirmElement} onDragEditCoords={handleDragCoords} onApplyManualCoords={handleApplyCoords} rejectionBanner={severeErrorSummary?.dominantError ?? null} /> : <StepLoading />
        })()}

        {/* Step 3 - Coteja completitud */}
        {currentStep === 3 && processedData && (
          <Suspense fallback={<StepLoading />}>
            {cotejoLoading && (
              <div data-testid="cotejo-loading" style={{ textAlign: 'center', padding: '48px 0', color: '#a1a1aa', fontSize: '13px' }}>
                <StepLoading />
                <p style={{ marginTop: '8px' }}>Cotejando catálogo oficial…</p>
              </div>
            )}
            {cotejoError && (
              <div data-testid="cotejo-error" style={{ textAlign: 'center', padding: '32px', color: '#f87171', fontSize: '13px' }}>
                Error al cotejar: {cotejoError}
              </div>
            )}
            {cotejoResult && (
              <Step3CotejaPage
                municipio={extractionData?.metadata.municipality ?? cotejoResult.municipio}
                provincia={extractionData?.metadata.province ?? ''}
                ine5={cotejoResult.ine5}
                items={itemsCotejoP3}
                registrosPlan={registrosPlanP3}
                decisiones={decisionesFila}
                onDecidir={decidirFila}
                onConfirm={(_confirmed) => handleNext()}
                onBack={handlePrev}
              />
            )}
          </Suspense>
        )}

        {/* Step 4 - Exporta (antes Descarga) */}
        {/* P4IDENT (DOP-3 a): cinturón. Si las filas no son del documento
            activo, no se monta el exportador. Con `extractionData` nulo no
            hay con qué comparar y se exporta como siempre. Desde RECEXT
            «Recuperar» repone `extractionData` y la comparación también
            corre tras restaurar desde caché (los `id` sobreviven a ella). */}
        {currentStep === 4 && processedData && (() => {
          const pertenencia = filasPertenecenAlDocumento(processedData.infrastructures, extractionData?.infrastructures)
          if (!pertenencia.pertenecen) {
            return (
              <div
                role="alert"
                data-testid="p4-filas-ajenas"
                style={{
                  maxWidth: '760px',
                  margin: '48px auto',
                  padding: '20px 24px',
                  borderRadius: '8px',
                  background: 'rgba(127, 29, 29, 0.25)',
                  border: '1px solid rgba(220, 38, 38, 0.5)',
                  color: '#fecaca',
                  fontSize: '14px',
                  lineHeight: 1.5,
                }}
              >
                <p style={{ margin: '0 0 12px 0', fontWeight: 600, color: '#fca5a5' }}>
                  No se puede exportar
                </p>
                <p style={{ margin: '0 0 16px 0' }}>
                  {mensajeFilasAjenas(extractionData?.file.name)}
                  {' '}({pertenencia.ajenas} de {pertenencia.total} filas ajenas)
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentStep(1)
                    navigate('/wizard/1', { replace: true })
                  }}
                  className="text-red-200 border-red-700 hover:bg-red-900/50"
                >
                  Volver a Normaliza
                </Button>
              </div>
            )
          }
          return (
            <Suspense fallback={<StepLoading />}>
              <Step4ExportaPage 
                infrastructures={processedData.infrastructures}
                metadata={toExportMetadata(processedData.metadata)}
                resultadoCotejo={cotejoParaP4}
                onBack={handlePrev}
                onGoToValida={() => handleStepClick(2)}
              />
            </Suspense>
          )
        })()}

        {/* Footer compacto */}
        <footer style={{
          textAlign: 'center',
          marginTop: '12px',
          // T2b: 80px eran el hueco del StepperFloating; queda el aire estándar
          paddingBottom: '24px',
          fontSize: '12px',
          color: '#71717a',
        }}>
          Compatible con{' '}
          <a 
            href="https://qgis.org" 
            target="_blank" 
            rel="noopener noreferrer" 
            style={{ color: '#2DD4BF', textDecoration: 'none' }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            QGIS
          </a>
          {' '}· Salida ETRS89 / UTM30N
        </footer>
      </main>

      {/* T1CB: StepperFloating retirado — navegación de pasos en la fila 2
          (WizardContextBar, cabecera B). El componente sigue vivo para las
          páginas de debug; su poda definitiva cae en T2 si procede. */}

      {/* BUG-7b: modal edición manual nombre/dirección */}
      {editingInfraId && (() => {
        const allInfras = geocodedResults.length > 0 ? geocodedResults : normalizedResults
        const infra = allInfras.find(r => r.id === editingInfraId)
        return (
          <InlineEditForm
            infraId={editingInfraId}
            currentName={infra?.nombre ?? ''}
            currentAddress={infra?.direccion ?? infra?.direccionAlt ?? ''}
            onSave={handleSaveEdit}
            onCancel={() => setEditingInfraId(null)}
          />
        )
      })()}

      {/* Lote C1: editor manual de coordenadas UTM30 (faldón del mapa) */}
      {editingCoordsInfraId && (() => {
        const allInfras = geocodedResults.length > 0 ? geocodedResults : normalizedResults
        const infra = allInfras.find(r => r.id === editingCoordsInfraId)
        return (
          <CoordEditPanel
            infraId={editingCoordsInfraId}
            infraName={infra?.nombre ?? undefined}
            currentX={infra?.xFinal ?? null}
            currentY={infra?.yFinal ?? null}
            onApply={handleApplyCoords}
            onCancel={() => setEditingCoordsInfraId(null)}
          />
        )
      })()}
    </div>
  )
}

export default WizardPageV2
